import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import type { User } from '@shared/types'
import { ApiError, AUTH_EXPIRED, api, get, post } from '../lib/api'

export interface PublicSettings {
  orgName: string
  timezone: string
  geo: { minAccuracyM: number; dwellSeconds: number }
  photo: { maxDimension: number; quality: number }
  encouragePhoto: boolean
  pushConfigured: boolean
}

const FALLBACK_SETTINGS: PublicSettings = {
  orgName: 'Windmill Construction',
  timezone: 'UTC',
  geo: { minAccuracyM: 100, dwellSeconds: 120 },
  photo: { maxDimension: 1600, quality: 0.75 },
  encouragePhoto: true,
  pushConfigured: false,
}

const USER_CACHE = 'wm-user'
const SETTINGS_CACHE = 'wm-settings'

/**
 * The last known signed-in user.
 *
 * Opening the app in a basement or a rural lot must not dump the crew at a
 * sign-in screen they cannot complete. The session cookie is still valid; only
 * the probe failed. So a cached identity carries the UI until the network comes
 * back — and a real 401 clears it immediately.
 */
function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function writeCache(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode or a full quota — the app still works, just not offline.
  }
}

interface SessionApi {
  user: User | null
  settings: PublicSettings
  loading: boolean
  /** True when we are running on a cached identity because the probe failed. */
  offlineSession: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  setUser: (user: User) => void
}

const SessionContext = createContext<SessionApi | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => readCache<User>(USER_CACHE))
  const [settings, setSettings] = useState<PublicSettings>(
    () => readCache<PublicSettings>(SETTINGS_CACHE) ?? FALLBACK_SETTINGS,
  )
  const [loading, setLoading] = useState(true)
  const [offlineSession, setOfflineSession] = useState(false)

  const loadSettings = useCallback(async () => {
    try {
      const next = await get<PublicSettings>('/api/settings/public')
      setSettings(next)
      writeCache(SETTINGS_CACHE, next)
    } catch {
      // Keep whatever we had; the timezone fallback is only cosmetic.
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await api<{ user: User }>('/api/auth/me', { quiet: true })
      setUser(me)
      setOfflineSession(false)
      writeCache(USER_CACHE, me)
      await loadSettings()
    } catch (err) {
      // A 401 is a real answer: the session is gone, so clear it. Anything else
      // is a network failure, and the cached identity stands.
      if (err instanceof ApiError && err.status === 401) {
        setUser(null)
        setOfflineSession(false)
        localStorage.removeItem(USER_CACHE)
      } else {
        setOfflineSession(readCache<User>(USER_CACHE) !== null)
      }
    } finally {
      setLoading(false)
    }
  }, [loadSettings])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onExpired = () => {
      setUser(null)
      setOfflineSession(false)
      localStorage.removeItem(USER_CACHE)
    }
    window.addEventListener(AUTH_EXPIRED, onExpired)
    return () => window.removeEventListener(AUTH_EXPIRED, onExpired)
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { user: me } = await post<{ user: User }>('/api/auth/login', { email, password })
    setUser(me)
    setOfflineSession(false)
    writeCache(USER_CACHE, me)
    await loadSettings()
  }, [loadSettings])

  const signOut = useCallback(async () => {
    await post('/api/auth/logout').catch(() => undefined)
    setUser(null)
    setOfflineSession(false)
    localStorage.removeItem(USER_CACHE)
  }, [])

  const value = useMemo<SessionApi>(
    () => ({ user, settings, loading, offlineSession, signIn, signOut, refresh, setUser }),
    [user, settings, loading, offlineSession, signIn, signOut, refresh],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}
